import React, { useState } from 'react'
import * as APIs from "./api";
import { loaderStart, loaderEnd } from '../../components/loader'
import BackButton from '../../components/backButton'

import '../Question-bank/questionbank.css'
import './certificateCheck.css'

/**
 * Public certificate verification: enter a certificate number, see who it was
 * issued to.
 *
 * Every failure used to raise alert("Incorrect certificate number.") — including
 * the server being unreachable — so a genuine certificate could be reported as
 * fake. "Not found" and "could not check" are now separate outcomes, shown on
 * the page rather than in a browser alert.
 */
const CertificateCheck = () => {

    const [cert, setCert] = useState("")
    // { status: 'found', data } | { status: 'not_found', number } | { status: 'error' }
    const [outcome, setOutcome] = useState(null)

    const getData = async (e) => {
        e.preventDefault()
        const certificateNumber = cert.trim()
        if (!certificateNumber) return

        loaderStart()
        let res = await APIs.getCertInfo({ certificateNumber })
        loaderEnd()

        if (res?.code === 1 && res.data) {
            setOutcome({ status: 'found', data: res.data })
        }
        // The backend answers a lookup that ran but matched nothing with exactly
        // this message; anything else (network, server or database error) means
        // the check itself did not happen.
        else if (res?.code === 0 && res?.message === 'Invalid certificate number') {
            setOutcome({ status: 'not_found', number: certificateNumber })
        }
        else {
            setOutcome({ status: 'error' })
        }
    }

    return (
        <div className='qb_page cert_page'>
            <BackButton to='/' />
            <div className='qb_header'>
                <h1 className='qb_title'>Certificate Verification</h1>
                <p className='qb_subtitle'>
                    Check that a Computer Competency Test certificate was issued by
                    BIPARD. Enter the certificate number exactly as printed on it.
                </p>
            </div>

            <div className='ui_panel cert_panel'>
                <div className='ui_panel_head'>
                    <h2 className='ui_panel_title'>Verify a certificate / प्रमाण-पत्र सत्यापन</h2>
                </div>

                <form className='ui_panel_body' onSubmit={getData}>
                    <div className='ui_field'>
                        <label className='ui_label' htmlFor='certificate_number'>
                            <span className='ui_required'>*</span>
                            <i className='fa fa-certificate' aria-hidden='true'></i>
                            Certificate Number / प्रमाण-पत्र संख्या
                        </label>
                        <div className='cert_search'>
                            <input
                                id='certificate_number'
                                required
                                value={cert}
                                onChange={(e) => setCert(e.target.value)}
                                type='text'
                                className='ui_input'
                                placeholder='Certificate number as printed'
                                autoComplete='off'
                            />
                            <button type='submit' className='ui_btn ui_btn_primary'>
                                <i className='fa fa-search' aria-hidden='true'></i> Verify
                            </button>
                        </div>
                    </div>

                    {outcome?.status === 'found' ? (
                        <div className='cert_result cert_result_ok' role='status'>
                            <p className='cert_result_title'>
                                <i className='fa fa-check-circle' aria-hidden='true'></i>
                                Valid certificate
                            </p>
                            <dl className='cert_details'>
                                <dt>Certificate Number</dt>
                                <dd>{outcome.data.certificateNumber}</dd>
                                <dt>Name</dt>
                                <dd>{outcome.data.name}</dd>
                                <dt>Department</dt>
                                <dd>{outcome.data.department || '—'}</dd>
                                <dt>Exam Date</dt>
                                <dd>{outcome.data.date}</dd>
                            </dl>
                        </div>
                    ) : outcome?.status === 'not_found' ? (
                        <div className='cert_result cert_result_bad' role='alert'>
                            <p className='cert_result_title'>
                                <i className='fa fa-times-circle' aria-hidden='true'></i>
                                No certificate found
                            </p>
                            <p className='cert_result_text'>
                                No certificate with the number <b>{outcome.number}</b> has been
                                issued. Check the number and try again.
                            </p>
                        </div>
                    ) : outcome?.status === 'error' ? (
                        <div className='cert_result cert_result_warn' role='alert'>
                            <p className='cert_result_title'>
                                <i className='fa fa-exclamation-triangle' aria-hidden='true'></i>
                                Could not verify right now
                            </p>
                            <p className='cert_result_text'>
                                The verification service did not respond. This does not mean the
                                certificate is invalid — please try again shortly.
                            </p>
                        </div>
                    ) : null}
                </form>
            </div>
        </div>
    )
}

export default CertificateCheck
